import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  /** Label used in the default fallback UI, e.g. "Orchard Map" */
  moduleName: string;
  /** Optional custom fallback element. Replaces the default fallback when provided. */
  fallback?: ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * ErrorBoundary
 *
 * Catches unhandled React render errors, reports them to Sentry/GlitchTip,
 * and displays either a custom or default module-specific fallback UI.
 * Errors in the observability infrastructure itself never crash the app.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    try {
      Sentry.captureException(error);
    } catch {
      // Sentry reporting must never crash the app
    }
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className="alert alert-warning m-3" role="alert">
        <strong>{this.props.moduleName}</strong> — something went wrong loading this section.
        Please refresh the page. If the problem persists, contact support.
      </div>
    );
  }
}

export default ErrorBoundary;
